import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Skeleton,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Circle,
  PersonOff,
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
} from "@mui/icons-material";
import theme from "../../theme/theme";

// Status color definitions
const STATUS_COLORS = {
  completed: {
    bg: "#E8F5E9",
    color: "#2E7D32",
    border: "#A5D6A7",
    label: "Completed",
    icon: CheckCircle,
  },
  pending: {
    bg: "#E3F2FD",
    color: "#1565C0",
    border: "#90CAF9",
    label: "Pending",
    icon: Schedule,
  },
  cancelled: {
    bg: "#FFEBEE",
    color: "#C62828",
    border: "#EF9A9A",
    label: "Cancelled",
    icon: Cancel,
  },
  unassigned: {
    bg: "#FFF3E0",
    color: "#E65100",
    border: "#FFCC80",
    label: "Pending (Unassigned)",
    icon: PersonOff,
  },
  delayed: {
    bg: "#FCE4EC",
    color: "#AD1457",
    border: "#F48FB1",
    label: "Delayed",
    icon: Warning,
  },
};

const getEntryStatus = (entry) => {
  if (entry.service_status === "completed") return "completed";
  if (entry.service_status === "cancelled") return "cancelled";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const serviceDate = new Date(entry.service_date);
  serviceDate.setHours(0, 0, 0, 0);

  if (entry.service_status === "pending" && serviceDate < today)
    return "delayed";
  if (entry.service_status === "pending" && !entry.service_by)
    return "unassigned";
  return "pending";
};

// Mixed background for multiple statuses
const getCellBg = (entries) => {
  if (entries.length === 0) return "transparent";
  if (entries.length === 1)
    return STATUS_COLORS[getEntryStatus(entries[0])]?.bg;
  // Multiple: use a subtle neutral + left border accents
  return "#F5F5F5";
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ServiceCalendar = ({ entries = [], loading = false, onEntryClick }) => {
  const [viewDate, setViewDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverEntries, setPopoverEntries] = useState([]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  // Map entries by date string
  const entryMap = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      const dateStr = entry.service_date?.split("T")[0];
      if (dateStr) {
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push(entry);
      }
    });
    return map;
  }, [entries]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({
        day: d,
        dateStr,
        entries: entryMap[dateStr] || [],
      });
    }

    return days;
  }, [year, month, entryMap]);

  const handleCellClick = (e, cellEntries) => {
    if (cellEntries.length === 0) return;
    if (cellEntries.length === 1) {
      // Single entry — open directly
      if (onEntryClick) onEntryClick(cellEntries[0]);
    } else {
      // Multiple entries — show popover to pick
      setAnchorEl(e.currentTarget);
      setPopoverEntries(cellEntries);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverEntries([]);
  };

  const handlePopoverEntryClick = (entry) => {
    handlePopoverClose();
    if (onEntryClick) onEntryClick(entry);
  };

  if (loading) {
    return <Skeleton variant="rounded" height={400} />;
  }

  return (
    <Box>
      {/* Month Navigation */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <IconButton onClick={prevMonth} size="small">
          <ChevronLeft />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="700">
          {MONTHS[month]} {year}
        </Typography>
        <IconButton onClick={nextMonth} size="small">
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Day Headers */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
          mb: 0.5,
        }}
      >
        {DAYS.map((day) => (
          <Box key={day} sx={{ textAlign: "center", py: 0.8 }}>
            <Typography
              variant="caption"
              fontWeight="700"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0.5,
        }}
      >
        {calendarDays.map((cell, idx) =>
          cell === null ? (
            <Box key={`empty-${idx}`} sx={{ minHeight: 64 }} />
          ) : (
            <Box
              key={cell.dateStr}
              onClick={(e) => handleCellClick(e, cell.entries)}
              sx={{
                minHeight: 64,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor:
                  cell.entries.length > 0 ? "transparent" : "divider",
                p: 0.5,
                position: "relative",
                backgroundColor: getCellBg(cell.entries),
                transition: "all 0.15s ease",
                cursor: cell.entries.length > 0 ? "pointer" : "default",
                "&:hover":
                  cell.entries.length > 0
                    ? {
                        opacity: 0.8,
                        transform: "scale(1.03)",
                      }
                    : {},
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    color:
                      cell.entries.length === 1
                        ? STATUS_COLORS[getEntryStatus(cell.entries[0])]?.color
                        : cell.entries.length > 1
                          ? "text.primary"
                          : "text.secondary",
                  }}
                >
                  {cell.day}
                </Typography>
                {cell.entries.length > 1 && (
                  <Chip
                    label={cell.entries.length}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 9,
                      fontWeight: 800,
                      backgroundColor: theme.palette.primary.main,
                      color: "#fff",
                      "& .MuiChip-label": { px: 0.5 },
                    }}
                  />
                )}
              </Box>

              {/* Show status dots for each entry */}
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 0.2, mt: 0.3 }}
              >
                {cell.entries.map((entry) => {
                  const status = getEntryStatus(entry);
                  const statusDef = STATUS_COLORS[status];
                  const StatusIcon = statusDef.icon;

                  return (
                    <Tooltip
                      key={entry.id}
                      title={
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600 }}
                          >
                            {statusDef.label}
                          </Typography>
                          {entry.service?.project?.project_id && (
                            <Typography variant="caption" display="block">
                              Project: {entry.service.project.project_id}
                            </Typography>
                          )}
                          {entry.service_employee?.name && (
                            <Typography variant="caption" display="block">
                              Assigned: {entry.service_employee.name}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                          backgroundColor: statusDef.bg,
                          borderRadius: 0.5,
                          px: 0.3,
                          py: 0.1,
                        }}
                      >
                        <StatusIcon
                          sx={{ fontSize: 10, color: statusDef.color }}
                        />
                        {cell.entries.length <= 2 && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 8,
                              fontWeight: 600,
                              color: statusDef.color,
                              lineHeight: 1,
                            }}
                          >
                            {statusDef.label.split(" ")[0]}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ),
        )}
      </Box>

      {/* Multi-entry Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 240, maxWidth: 320 },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" fontWeight="700" color="text.secondary">
            {popoverEntries.length} service entries on this day
          </Typography>
        </Box>
        <List dense sx={{ py: 0.5 }}>
          {popoverEntries.map((entry) => {
            const status = getEntryStatus(entry);
            const statusDef = STATUS_COLORS[status];
            const StatusIcon = statusDef.icon;
            return (
              <ListItemButton
                key={entry.id}
                onClick={() => handlePopoverEntryClick(entry)}
                sx={{
                  borderRadius: 1,
                  mx: 1,
                  mb: 0.5,
                  "&:hover": { backgroundColor: statusDef.bg },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <StatusIcon sx={{ fontSize: 18, color: statusDef.color }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{ fontSize: 13 }}
                    >
                      {entry.service?.project?.project_id
                        ? `Project #${entry.service.project.project_id}`
                        : `Service #${entry.service_id}`}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{ color: statusDef.color }}
                    >
                      {statusDef.label}
                      {entry.service_employee?.name
                        ? ` • ${entry.service_employee.name}`
                        : ""}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Popover>

      {/* Legend */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mt: 2.5,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        {Object.entries(STATUS_COLORS).map(([key, val]) => (
          <Box
            key={key}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Circle sx={{ fontSize: 10, color: val.color }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {val.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ServiceCalendar;
